<script setup lang="ts">
interface Props {
  dynamicId?: string;
  value?: string;
  width?: string;
  index?: number;
  maxLength?: number;
  rows?: number;
  title?: string;
  name?: string;
  subtitle?: string;
  placeHolder?: string;
  isUppercase?: boolean;
  disabled?: boolean;
  errorMessage?: string;
  isRequired?: boolean;
  showTitle?: boolean;
  type?: string;
  login?: boolean;
  autocomplete?: string;
}

const props = withDefaults(defineProps<Props>(), {
  dynamicId: "",
  value: "",
  width: "99%",
  index: 0,
  maxLength: 250,
  rows: 3,
  title: "",
  name: "",
  subtitle: "",
  placeHolder: "Please enter some text",
  isUppercase: false,
  disabled: false,
  errorMessage: "",
  isRequired: false,
  showTitle: true,
  type: "text",
  login: false,
  autocomplete: "off",
});

const emit = defineEmits<{
  returnValue: [
    field: {
      type: string;
      index: number;
      value: string;
      required: boolean;
      title: string;
      login: boolean;
    },
  ];
  enterPressed: [value: boolean];
}>();

const stripHtml = (str: string) => str?.replace(/<[^>]*>?/gm, "") || "";

const inputValue = ref(
  props.isUppercase ? stripHtml(props.value).toUpperCase() : stripHtml(props.value),
);

let timer: ReturnType<typeof setTimeout> | null = null;

// Emails go up lower-cased, so both sides of the comparison below are normalised
// — otherwise the parent's echo of one reads as a fresh change and emits twice.
const normalised = (value: string) => (props.type === "email" ? value.toLocaleLowerCase() : value);

// The value the parent last received. `timer` is NOT a reliable proxy for "the
// parent is behind": the watcher that arms the debounce runs a microtask after
// the `input` event, so the DOM can be ahead of the parent while `timer` is
// still null — autofill and the first `isUppercase` render both land there. So
// compare values, not timer state.
let lastEmitted = normalised(props.value);

const isTitleVisible = computed(() => props.title !== "" && props.showTitle);
const showError = computed(
  () => props.errorMessage.length > 0 || (props.isRequired && inputValue.value === ""),
);

watch(
  () => props.value,
  (to) => {
    if (to !== undefined) {
      inputValue.value = props.isUppercase ? stripHtml(to).toUpperCase() : stripHtml(to);
    }
  },
);

watch(inputValue, () => {
  handleKeyup();
});

function emitValue() {
  lastEmitted = normalised(inputValue.value);
  emit("returnValue", {
    type: props.type,
    index: props.index,
    value: lastEmitted,
    required: props.isRequired,
    title: props.title,
    login: props.login,
  });
}

// Value-guarded, not just flush(): a queued `inputValue` watcher can arm a timer
// after flush() has already emitted, and that timer would otherwise send the same
// value again once the submit had read it.
function emitIfChanged() {
  if (normalised(inputValue.value) === lastEmitted) return;
  emitValue();
}

function handleKeyup() {
  if (timer) clearTimeout(timer);
  timer = setTimeout(() => {
    timer = null;
    emitIfChanged();
  }, 500);
}

/**
 * Hand the parent our current value now, cancelling any pending debounce.
 *
 * Without it the parent is 500 ms behind the last keystroke, so anything
 * submitting inside that window reads a truncated value. Every submit path must
 * call this first; exposed for parents that submit without going through
 * `enterPressed` (a button click, a native form submission). A no-op when the
 * parent already holds this value — see `emitIfChanged`. Ticket 1143.
 */
function flush() {
  if (timer) {
    clearTimeout(timer);
    timer = null;
  }
  emitIfChanged();
}

function enterPressed() {
  flush();
  emit("enterPressed", true);
}

// A click on a submit button blurs the focused field first (mousedown → blur →
// mouseup → click), so flushing here fixes every button-submit consumer at once
// rather than each page having to remember to call flush() itself.
function handleBlur() {
  flush();
}

// A pending timer used to outlive the component and emit into a dead parent.
onBeforeUnmount(() => {
  if (timer) clearTimeout(timer);
});

defineExpose({ flush });
</script>

<template>
  <div
    class="text-container"
    :style="`width: ${width}`"
  >
    <span
      v-if="isTitleVisible"
      class="text-container__title"
    >
      {{ title }}
      <i v-if="isRequired">*</i>
      <span class="text-container__subtitle small-text">
        {{ subtitle }}
      </span>
    </span>
    <textarea
      v-if="type === 'textarea'"
      :id="dynamicId"
      v-model="inputValue"
      :maxlength="maxLength"
      :rows="rows"
      class="text-container__input"
      :class="[showError && 'input-error', disabled && 'disabled']"
      :placeholder="placeHolder"
      :name="name"
      :autocomplete="autocomplete"
      @blur="handleBlur"
      @keyup.enter="enterPressed"
    />
    <input
      v-else
      :id="dynamicId"
      v-model="inputValue"
      class="text-container__input"
      :class="[showError && 'input-error', disabled && 'disabled']"
      :placeholder="placeHolder"
      :type="type"
      :name="name"
      :autocomplete="autocomplete"
      :disabled="disabled"
      @blur="handleBlur"
      @keyup.enter="enterPressed"
    />
    <BannerError :message="showError ? errorMessage : ''" />
  </div>
</template>

<style lang="scss">
.text-container {
  &__input {
    cursor: text;
    width: 100%;
  }
  &__title,
  &__error {
    display: flex;
    align-items: center;
  }
  &__title {
    color: var(--color-grey-1);
    i {
      color: var(--color-pink-1);
    }
  }
  &__subtitle {
    margin-left: 10px;
    color: var(--color-grey-2) !important;
  }
}
</style>
